/**
 * Article contents, table labels, and shoppable captions.
 *
 * Builds a fixed contents rail from h2s at >= 1280px and a collapsed
 * "in this article" block below the byline when there are enough chapters.
 * Labels come from data-toc-label on .article .rte so the script stays
 * locale-agnostic.
 */
(function () {
  'use strict';

  const rte = document.querySelector('.article .rte');
  if (!rte || rte.dataset.readingEnhanced) return;
  rte.dataset.readingEnhanced = 'true';

  enhanceCaptions(rte);
  promoteHeaderRows(rte);
  labelTables(rte);
  wrapTables(rte);
  markCallouts(rte);
  markGlance(rte);

  const headings = Array.from(rte.querySelectorAll('h2'));
  const MIN_CHAPTERS = 4;
  if (headings.length < MIN_CHAPTERS) return;

  const headerOffset = () => {
    const header = document.querySelector('.header-section') || document.querySelector('.header');
    const extra = window.innerWidth < 1280 ? 64 : 48;
    return Math.round((header ? header.getBoundingClientRect().height : 72) + extra) + 'px';
  };

  const usedIds = new Set();
  const slugify = (str) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const applyOffsets = () => {
    const offset = headerOffset();
    headings.forEach((h) => {
      h.style.scrollMarginTop = offset;
    });
  };

  const items = headings.map((h) => {
    if (!h.id) {
      const base = slugify(h.textContent || '') || 'section';
      let candidate = base;
      let i = 1;
      while (usedIds.has(candidate) || document.getElementById(candidate)) {
        candidate = base + '-' + ++i;
      }
      h.id = candidate;
    }
    usedIds.add(h.id);
    return { id: h.id, text: (h.dataset.tocTitle || h.textContent || '').trim(), el: h };
  });
  applyOffsets();

  const label = rte.dataset.tocLabel || 'In this article';

  const aside = document.createElement('aside');
  aside.className = 'article-toc';
  aside.setAttribute('aria-label', label);

  const title = document.createElement('p');
  title.className = 'article-toc__title';
  title.textContent = label;

  const list = document.createElement('ol');
  list.className = 'article-toc__list';
  items.forEach(({ id, text }) => {
    const li = document.createElement('li');
    li.className = 'article-toc__item';
    li.appendChild(makeLink(id, text, 'article-toc__link'));
    list.appendChild(li);
  });
  aside.appendChild(title);
  aside.appendChild(list);
  document.body.appendChild(aside);

  const details = document.createElement('details');
  details.className = 'article-toc-inline';
  const summary = document.createElement('summary');
  summary.textContent = label + ' \u00b7 ' + items.length;
  details.appendChild(summary);
  items.forEach(({ id, text }) => {
    details.appendChild(makeLink(id, text, 'article-toc-inline__link'));
  });

  const entry = document.createElement('nav');
  entry.className = 'article-entry-nav';
  entry.setAttribute('aria-label', label);
  const comparison = rte.querySelector('table');
  const featured = items.find(({ el }) => el.hasAttribute('data-quick-link')) ||
    (comparison && items.filter(({ el }) => el.compareDocumentPosition(comparison) & Node.DOCUMENT_POSITION_FOLLOWING).pop());
  if (featured) {
    entry.appendChild(makeLink(featured.id, featured.text, 'article-entry-nav__featured'));
  }
  entry.appendChild(details);
  const byline = document.querySelector('.article-hero__meta');
  if (byline) {
    byline.insertAdjacentElement('afterend', entry);
  } else if (rte.firstChild) {
    rte.insertBefore(entry, rte.firstChild);
  } else {
    rte.appendChild(entry);
  }

  const dock = document.createElement('nav');
  dock.className = 'article-reading-nav';
  dock.setAttribute('aria-label', label);
  const dockDetails = document.createElement('details');
  const dockSummary = document.createElement('summary');
  const dockCurrent = document.createElement('span');
  dockCurrent.className = 'article-reading-nav__current';
  const dockLabel = document.createElement('span');
  dockLabel.textContent = label;
  dockSummary.append(dockLabel, dockCurrent);
  const dockLinks = document.createElement('div');
  dockLinks.className = 'article-reading-nav__links';
  items.forEach(({ id, text }) => dockLinks.appendChild(makeLink(id, text, 'article-toc-inline__link')));
  dockDetails.append(dockSummary, dockLinks);
  dock.appendChild(dockDetails);
  document.body.appendChild(dock);

  const links = Array.from(document.querySelectorAll('.article-toc__link, .article-toc-inline__link, .article-entry-nav__featured'));
  links.forEach((link) => link.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    details.open = false;
    dockDetails.open = false;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (target) {
      target.tabIndex = -1;
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
  }));
  dock.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dockDetails.open) {
      dockDetails.open = false;
      dockSummary.focus();
    }
  });
  document.addEventListener('click', (event) => {
    if (!dock.contains(event.target)) dockDetails.open = false;
  });
  dock.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!dock.contains(document.activeElement)) dockDetails.open = false;
    });
  });
  let ticking = false;

  const activationOffset = () => {
    const header = document.querySelector('.header-section') || document.querySelector('.header');
    const titled = document.querySelector('reading-progress.is-titled');
    const headerH = header ? header.getBoundingClientRect().height : 72;
    return Math.round(headerH + (window.innerWidth < 1280 ? 64 : titled ? 48 : 20));
  };

  function updateActive() {
    const offset = activationOffset();
    const rteRect = rte.getBoundingClientRect();
    const isReading = rteRect.top <= offset && rteRect.bottom >= offset;
    aside.classList.toggle('is-visible', isReading);
    dock.classList.toggle('is-visible', isReading);
    if (!isReading) dockDetails.open = false;
    document.documentElement.classList.toggle('is-reading-article', isReading);

    let activeIndex = 0;
    for (let i = 0; i < items.length; i++) {
      if (items[i].el.getBoundingClientRect().top <= offset + 2) {
        activeIndex = i;
      } else {
        break;
      }
    }
    links.forEach((link) => {
      const active = link.getAttribute('href') === '#' + items[activeIndex].id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    dockCurrent.textContent = items[activeIndex].text;
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(updateActive);
        ticking = true;
      }
    },
    { passive: true }
  );
  window.addEventListener('resize', () => { applyOffsets(); updateActive(); }, { passive: true });

  updateActive();

  function makeLink(id, text, className) {
    const a = document.createElement('a');
    a.href = '#' + id;
    a.className = className;
    a.textContent = text;
    return a;
  }
})();

function markCallouts(rte) {
  rte.querySelectorAll('p').forEach((paragraph) => {
    const lead = paragraph.querySelector(':scope > strong:first-child, :scope > b:first-child');
    if (!lead) return;
    const label = (lead.textContent || '').trim();
    if (!label.endsWith(':') || label.length > 42) return;
    paragraph.classList.add('article-callout');
  });
}

function markGlance(rte) {
  const table = rte.querySelector('table');
  const firstH2 = rte.querySelector('h2');
  if (!table) return;
  if (firstH2 && !(table.compareDocumentPosition(firstH2) & Node.DOCUMENT_POSITION_FOLLOWING)) {
    return;
  }
  table.classList.add('article-glance');
  const label = (rte.dataset.glanceLabel || '').trim();
  if (!label) return;
  if (table.previousElementSibling && table.previousElementSibling.classList.contains('article-glance__label')) {
    return;
  }
  const heading = document.createElement('p');
  heading.className = 'article-glance__label';
  heading.textContent = label;
  table.parentNode.insertBefore(heading, table);
}

function enhanceCaptions(rte) {
  rte.querySelectorAll('figure').forEach((figure) => {
    const link = figure.querySelector('a[href]');
    const img = figure.querySelector('img');
    let caption = figure.querySelector('figcaption');

    if (!caption) {
      const alt = ((img && img.getAttribute('alt')) || '').trim();
      const fallback = (link && (link.getAttribute('aria-label') || '').trim()) || '';
      const text = alt || fallback;
      if (!text) return;
      caption = document.createElement('figcaption');
      caption.textContent = text;
      figure.appendChild(caption);
    }

    if (!link || caption.querySelector('a')) return;
    const shop = document.createElement('a');
    shop.href = link.href;
    shop.className = 'article-caption__shop';
    while (caption.firstChild) shop.appendChild(caption.firstChild);
    caption.appendChild(shop);
  });
}

function promoteHeaderRows(rte) {
  rte.querySelectorAll('table').forEach((table) => {
    if (table.querySelector('thead')) return;
    const first = table.querySelector('tr');
    if (!first) return;
    const thead = document.createElement('thead');
    table.insertBefore(thead, table.firstChild);
    thead.appendChild(first);
    Array.from(first.children).forEach((cell) => {
      if (cell.tagName === 'TH') return;
      const th = document.createElement('th');
      while (cell.firstChild) th.appendChild(cell.firstChild);
      Array.from(cell.attributes).forEach((attr) => th.setAttribute(attr.name, attr.value));
      cell.replaceWith(th);
    });
  });
}

function wrapTables(rte) {
  rte.querySelectorAll('table').forEach((table) => {
    let wrap = table.parentElement;
    if (!wrap || !wrap.classList.contains('article-table')) {
      wrap = document.createElement('div');
      wrap.className = 'article-table';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    }
    const firstRow = table.querySelector('thead tr') || table.querySelector('tr');
    const cols = firstRow ? firstRow.children.length : 0;
    wrap.classList.toggle('article-table--compare', cols >= 3);
    wrap.classList.toggle('article-table--wide', cols >= 4);
    const rows = Array.from(table.querySelectorAll('tbody tr'));
    const simple = cols >= 3 && table.querySelectorAll('thead tr').length === 1 &&
      !table.querySelector('[colspan], [rowspan], table') &&
      rows.length > 0 && rows.every((row) => row.children.length === cols);
    if (simple) {
      table.classList.add('article-table-cards');
      // Preserve the table relationships when CSS stacks cells on small screens.
      table.setAttribute('role', 'table');
      table.querySelectorAll('thead, tbody').forEach((group) => group.setAttribute('role', 'rowgroup'));
      table.querySelectorAll('tr').forEach((row) => row.setAttribute('role', 'row'));
      table.querySelectorAll('thead th').forEach((cell) => {
        cell.scope = 'col';
        cell.setAttribute('role', 'columnheader');
      });
      rows.forEach((row) => Array.from(row.children).forEach((cell, index) => {
        cell.setAttribute('role', cell.tagName === 'TH' ? 'rowheader' : 'cell');
        if (index > 0 && cell.dataset.label) {
          const label = document.createElement('span');
          label.className = 'article-table-cards__label';
          label.setAttribute('aria-hidden', 'true');
          label.textContent = cell.dataset.label;
          cell.prepend(label);
        }
      }));
      wrap.classList.add('article-table--cards');
    } else {
      wrap.tabIndex = 0;
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-label', table.caption?.textContent || rte.dataset.glanceLabel || 'Table');
    }
  });
}

function labelTables(rte) {
  rte.querySelectorAll('table').forEach((table) => {
    const thead = Array.from(table.querySelectorAll('thead th')).map((th) => (th.textContent || '').trim());
    let heads = thead;
    let rows = Array.from(table.querySelectorAll('tbody tr'));
    if (!heads.length) {
      const allRows = Array.from(table.querySelectorAll('tr'));
      const first = allRows[0];
      if (!first) return;
      heads = Array.from(first.children).map((cell) => (cell.textContent || '').trim());
      rows = allRows.slice(1);
    }
    if (!rows.length) rows = Array.from(table.querySelectorAll('tr')).filter((row) => !row.closest('thead'));
    rows.forEach((row) => {
      Array.from(row.children).forEach((cell, index) => {
        if (cell.tagName === 'TH') return;
        if (heads[index]) cell.dataset.label = heads[index];
      });
    });
  });
}
